import 'reflect-metadata';

import { Pretend } from 'pretend';

import { DEFAULT_REGION, Environment, Region } from '../../types/core';
import { PaymentError } from '../payment-error';
import * as apis from './client';
import { ProblemDetail } from './dtos';

const LIVE_API_URL_BY_REGION: Partial<Record<Region, string>> = {
    'us-east-2': 'https://upc.payment.us.sportalliance.com/',
    'ap-southeast-4': 'https://upc.payment.au.sportalliance.com/'
};

interface Error {
    code: string;
    message: string;
}

export class PaymentApi {
    public userSessionToken?: string | null = null;
    public paymentRequestToken?: string | null = null;
    public environment: Environment;
    public region: Region;
    private baseUrl?: string;

    constructor(
        userSessionToken?: string,
        environment: Environment = 'sandbox',
        baseUrl?: string,
        region: Region = DEFAULT_REGION
    ) {
        this.userSessionToken = userSessionToken;
        this.environment = environment;
        this.baseUrl = baseUrl;
        this.region = region;
    }

    public get apiUrl() {
        if (this.baseUrl) {
            return this.baseUrl;
        }
        switch (this.environment) {
            case 'test':
                return 'https://upc.dev.payment.sportalliance.com/';
            case 'sandbox':
                return 'https://upc.sandbox.payment.sportalliance.com/';
            case 'live':
                return (
                    LIVE_API_URL_BY_REGION[this.region] ??
                    'https://upc.payment.sportalliance.com/'
                );
            default:
                return 'https://upc.dev.payment.sportalliance.com/';
        }
    }

    public get client() {
        return apis.getClient(this.apiUrl, undefined, (pretend) =>
            this.configureClient(pretend)
        );
    }

    private configureClient = (pretend: Pretend): Pretend => {
        pretend.requestInterceptor((request) => {
            request.options.headers = new Headers(request.options.headers);

            if (!request.options.headers.has('Content-Type')) {
                request.options.headers.set('Content-Type', 'application/json');
            } else if (
                request.options.headers.get('Content-Type') ===
                'multipart/form-data'
            ) {
                // note: strange but true, the fetch API sets the
                // content-type header on its own
                request.options.headers.delete('Content-Type');
            }

            if (this.userSessionToken) {
                request.options.headers.set(
                    'X-User-Payment-Session-Token',
                    this.userSessionToken
                );
            }

            return request;
        });

        pretend.decode(async (response) => {
            if (!response.ok) {
                let errorData: unknown;
                try {
                    errorData = await response.json();
                } catch {
                    throw new PaymentError(
                        `HTTP ${response.status}: ${response.statusText}`,
                        'API_ERROR',
                        undefined,
                        response.status
                    );
                }

                const { message, traceId } = this.extractErrorData(
                    errorData,
                    response.status
                );

                throw new PaymentError(
                    message,
                    'API_ERROR',
                    traceId,
                    response.status
                );
            }

            try {
                return (await response.json()) as unknown;
            } catch {
                return undefined;
            }
        });

        return pretend;
    };

    /**
     * Sets the payment request token after it's created
     */
    public setPaymentRequestToken(token: string): void {
        this.paymentRequestToken = token;
    }

    /**
     * Clears the payment request token, forcing a new one to be created
     */
    public clearPaymentRequestToken(): void {
        this.paymentRequestToken = null;
    }

    /**
     * Type guard to check if data is a ProblemDetail
     */
    private isProblemDetail(data: unknown): data is ProblemDetail {
        return (
            typeof data === 'object' &&
            data !== null &&
            ('errors' in data ||
                'detail' in data ||
                'title' in data ||
                'traceId' in data)
        );
    }

    /**
     * Extracts message and traceId from ProblemDetail response
     */
    private extractErrorData(
        errorData: unknown,
        status: number
    ): { message: string; traceId?: string } {
        if (this.isProblemDetail(errorData)) {
            const traceId =
                typeof errorData.traceId === 'string' ||
                typeof errorData.traceId === 'number'
                    ? String(errorData.traceId)
                    : undefined;

            if (errorData.errors && errorData.errors.length > 0) {
                const [firstError] = errorData.errors as Error[];
                return { message: firstError.message, traceId };
            }

            // Use detail or title as fallback when no errors array
            const message =
                errorData.detail || errorData.title || `API error ${status}`;
            return { message, traceId };
        }
        return {
            message: `Unexpected error format: ${JSON.stringify(errorData)}`
        };
    }
}
